import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../api';
import EventDetailsModal from '../components/EventDetailsModal';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toYmd(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
}

export default function VenueCalendar() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const qp = search.get('date');
    if (qp) {
      const [y, m] = qp.split('-').map(Number);
      if (y && m) return new Date(y, m - 1, 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });

  useEffect(() => {
    setLoading(true);
    api
      .get('/events/occupancy')
      .then((r) => setRows(r.data.events || []))
      .finally(() => setLoading(false));
  }, []);

  const qpDateKey = search.get('date');
  useEffect(() => {
    if (qpDateKey && /^\d{4}-\d{2}-\d{2}$/.test(qpDateKey)) {
      const [y, m] = qpDateKey.split('-').map(Number);
      setCursor(new Date(y, m - 1, 1));
    }
  }, [qpDateKey]);

  const byYmd = useMemo(() => {
    const m = {};
    rows.forEach((ev) => {
      const start = new Date(ev.date);
      const end = ev.endDate ? new Date(ev.endDate) : start;
      
      const current = new Date(start);
      while (current <= end) {
        const k = toYmd(current);
        if (!m[k]) m[k] = [];
        m[k].push(ev);
        current.setDate(current.getDate() + 1);
      }
    });
    return m;
  }, [rows]);

  const selectedYmd = qpDateKey && /^\d{4}-\d{2}-\d{2}$/.test(qpDateKey) ? qpDateKey : null;
  const selectedList = selectedYmd ? byYmd[selectedYmd] || [] : [];

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const pick = (day) => {
    if (!day) return;
    const ymd = `${year}-${pad2(month + 1)}-${pad2(day)}`;
    navigate(`${location.pathname}?date=${ymd}`);
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Venue calendar</div>
          <div className="page-subtitle">Campus-wide bookings by date — click a day to see venues in use</div>
        </div>
      </div>
      <div className="page-body">
        <div className="card" style={{ maxWidth: '64rem', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCursor(new Date(year, month - 1, 1))}>
              ← Prev
            </button>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.25rem', color: 'var(--text-1)' }}>
              {cursor.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCursor(new Date(year, month + 1, 1))}>
              Next →
            </button>
          </div>

          {loading ? (
            <div className="page-loading" style={{ minHeight: 200 }}>
              <div className="spinner" />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                  gap: 8,
                  textAlign: 'center',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 800,
                  color: 'var(--text-3)',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em'
                }}
              >
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 8 }}>
                {cells.map((day, idx) => {
                  if (day === null) return <div key={`e-${idx}`} />;
                  const ymd = `${year}-${pad2(month + 1)}-${pad2(day)}`;
                  const list = byYmd[ymd] || [];
                  const busy = list.length > 0;
                  const isSel = selectedYmd === ymd;
                  return (
                    <button
                      key={ymd}
                      type="button"
                      onClick={() => pick(day)}
                      style={{
                        minHeight: 72,
                        padding: '0.75rem',
                        borderRadius: 'var(--radius)',
                        border: `2px solid ${isSel ? 'var(--navy)' : 'var(--border-1)'}`,
                        background: isSel ? 'var(--surface-0)' : (busy ? 'var(--accent-muted)' : 'var(--surface-0)'),
                        boxShadow: isSel ? 'var(--shadow-sm)' : 'none',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ 
                        fontSize: '1rem', 
                        fontWeight: 700, 
                        color: isSel ? 'var(--navy)' : (busy ? 'var(--navy-deep)' : 'var(--text-2)')
                      }}>
                        {day}
                      </div>
                      {busy && (
                        <div style={{ 
                          fontSize: '0.625rem', 
                          fontWeight: 700,
                          background: 'rgba(0, 95, 167, 0.1)',
                          padding: '0.2rem 0.4rem',
                          borderRadius: '4px',
                          color: 'var(--navy-deep)'
                        }}>
                          {list.length} {list.length === 1 ? 'Event' : 'Events'}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {selectedYmd && (
          <div className="card" style={{ marginTop: '1.25rem', maxWidth: '64rem', margin: '1.25rem auto 0' }}>
            <h2 className="card-heading" style={{ marginBottom: '0.75rem' }}>
              {new Date(selectedYmd + 'T12:00:00').toLocaleDateString('en-IN', { dateStyle: 'full' })}
            </h2>
            {selectedList.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: 'var(--text-sm)' }}>No events — venues free this day.</p>
            ) : (
              <div className="stack-gap">
                {selectedList.map((ev) => (
                  <div 
                    key={ev._id} 
                    className="section-card" 
                    style={{ padding: '1.125rem', cursor: 'pointer' }}
                    onClick={() => setSelectedEventId(ev._id)}
                  >
                    <div style={{ fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--text-1)', fontSize: '1rem', lineHeight: 1.3 }}>{ev.title}</div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: 4, fontWeight: 600 }}>
                      {ev.venue}
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                      <span style={{ padding: '2px 6px', background: 'var(--gray-100)', borderRadius: '4px' }}>Host: <strong>{ev.faculty?.name || '—'}</strong></span>
                      <span>Guest: <strong>{ev.passCount || 0}</strong></span>
                      <span style={{ color: 'var(--green)', fontWeight: 700 }}>Arrived: {ev.arrivedCount || 0}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {selectedEventId && <EventDetailsModal eventId={selectedEventId} onClose={() => setSelectedEventId(null)} />}
      </div>
    </div>
  );
}
